import { compact } from 'lodash';
import * as z from 'zod';

import { Address, USStrictAddress } from '../../Address';
import { PayeeInput, PayeePaymentMethod, PayeeTaxType, PayeeType } from '../../payee/Payee';
import { OptionalPhoneNumber } from '../../Phone';
import { DueMonthDay } from '../../utils/Zod';

// Five ways "6/1" gets mangled before reaching this schema:
//   1. Excel re-saves the CSV with a year, producing "6/1/2025".
//   2. The file-import pipeline reads the CSV via xlsx with cellDates: true, which parses
//      date-shaped strings into JS Dates and then ISO-stringifies them to "2026-06-01".
//   3. The cell is text-formatted as "MMDD" (e.g. "0111"), with no separator.
//   4. Excel strips the leading zero from a numeric-looking MMDD cell, leaving 3 digits
//      ("0131" -> "131").
//   5. Vendor LATF files pad blank date cells to a fixed column width with spaces, so
//      "absent" arrives as "          " rather than "".
// Reshape any of these back to "M/d" so DueMonthDay's refine accepts it. The year is
// discarded — DueMonthDay is semantically month/day only.
const preprocessDueMonthDay = (val: unknown): unknown => {
  if (typeof val !== 'string') return val;
  const trimmed = val.trim();
  if (trimmed === '') return '';
  const slashMatch = trimmed.match(/^(\d{1,2}\/\d{1,2})\/\d{2,4}$/);
  if (slashMatch) return slashMatch[1];
  const isoMatch = trimmed.match(/^\d{4}-(\d{1,2})-(\d{1,2})$/);
  if (isoMatch) return `${parseInt(isoMatch[1], 10)}/${parseInt(isoMatch[2], 10)}`;
  const mmddMatch = trimmed.match(/^(\d{2})(\d{2})$/);
  if (mmddMatch) return `${parseInt(mmddMatch[1], 10)}/${parseInt(mmddMatch[2], 10)}`;
  const mddMatch = trimmed.match(/^(\d)(\d{2})$/);
  if (mddMatch) return `${parseInt(mddMatch[1], 10)}/${parseInt(mddMatch[2], 10)}`;
  return val;
};

const optionalDueMonthDay = z.preprocess(preprocessDueMonthDay, z.union([z.literal(''), DueMonthDay]).optional());

// LATF / xlsx exports pad string cells to fixed column widths with spaces. Trim before any
// other validation so "WI                  " is accepted as a state abbreviation, etc.
const preprocessTrim = (val: unknown): unknown => (typeof val === 'string' ? val.trim() : val);

// Excel strips leading zeros from numeric-looking ZIPs ("06183" -> "6183"). Zero-pad short
// numeric values back to 5 digits so US-strict address validation still succeeds.
const preprocessPostcode = (val: unknown): unknown => {
  if (typeof val !== 'string') return val;
  const trimmed = val.trim();
  if (/^\d{3,4}$/.test(trimmed)) return trimmed.padStart(5, '0');
  return trimmed;
};

const optionalString = z.preprocess(preprocessTrim, z.string().optional());
const optionalPostcode = z.preprocess(preprocessPostcode, z.string().optional());

export const BulkImportPayeesRow = z
  .object({
    name: z.string().min(1),
    payeeType: PayeeType,
    taxType: PayeeTaxType.optional(),
    externalPayeeId: optionalString,
    paymentMethod: PayeePaymentMethod.default('CHECK'),
    phone: OptionalPhoneNumber,
    addressLine1: optionalString,
    addressLine2: optionalString,
    addressLocality: optionalString,
    addressRegion: optionalString,
    addressPostcode: optionalPostcode,
    addressCountry: optionalString,
    dueDate1: optionalDueMonthDay,
    dueDate2: optionalDueMonthDay,
    dueDate3: optionalDueMonthDay,
    dueDate4: optionalDueMonthDay,
    isActive: z.boolean().default(true),
  })
  .superRefine((data, ctx) => {
    if (data.payeeType === 'TAX') {
      if (!data.taxType) {
        ctx.addIssue({ code: 'custom', path: ['taxType'], message: 'Required for tax payees' });
      }
      if (!data.externalPayeeId) {
        ctx.addIssue({ code: 'custom', path: ['externalPayeeId'], message: 'Required for tax payees' });
      }
    } else {
      if (data.taxType !== undefined) {
        ctx.addIssue({ code: 'custom', path: ['taxType'], message: 'Only allowed for tax payees' });
      }
      const dueDateKeys = ['dueDate1', 'dueDate2', 'dueDate3', 'dueDate4'] as const;
      for (const key of dueDateKeys) {
        if (data[key]) {
          ctx.addIssue({ code: 'custom', path: [key], message: 'Only allowed for tax payees' });
        }
      }
    }

    const addressFieldKeys = [
      'addressLine1',
      'addressLocality',
      'addressRegion',
      'addressPostcode',
      'addressCountry',
    ] as const;
    const anyAddressFilled = addressFieldKeys.some((key) => (data[key] ?? '').trim().length > 0);
    if (anyAddressFilled) {
      const result = USStrictAddress.safeParse(rowToAddress(data));
      if (!result.success) {
        const flatFieldByAddressKey: Record<string, (typeof addressFieldKeys)[number] | 'addressLine2'> = {
          line1: 'addressLine1',
          line2: 'addressLine2',
          locality: 'addressLocality',
          region: 'addressRegion',
          postcode: 'addressPostcode',
          country: 'addressCountry',
        };
        for (const issue of result.error.issues) {
          const top = issue.path[0];
          const flatKey = typeof top === 'string' ? flatFieldByAddressKey[top] : undefined;
          ctx.addIssue({ ...issue, path: flatKey ? [flatKey] : issue.path });
        }
      }
    }
  });

export type BulkImportPayeesRow = z.infer<typeof BulkImportPayeesRow>;

export const BULK_IMPORT_PAYEES_ROW_REQUIRED_FIELDS: ReadonlyArray<keyof BulkImportPayeesRow> = ['name', 'payeeType'];

const rowToAddress = (row: {
  addressLine1?: string;
  addressLine2?: string;
  addressLocality?: string;
  addressRegion?: string;
  addressPostcode?: string;
  addressCountry?: string;
}): z.infer<typeof Address> => ({
  line1: row.addressLine1 ?? '',
  line2: row.addressLine2 || undefined,
  locality: row.addressLocality ?? '',
  region: row.addressRegion ?? '',
  postcode: row.addressPostcode ?? '',
  country: row.addressCountry?.trim() || 'USA',
});

const isAddressBlank = (row: BulkImportPayeesRow): boolean =>
  !(row.addressLine1 ?? '').trim() &&
  !(row.addressLocality ?? '').trim() &&
  !(row.addressRegion ?? '').trim() &&
  !(row.addressPostcode ?? '').trim() &&
  !(row.addressCountry ?? '').trim();

export const bulkImportPayeesRowToPayeeInput = (row: BulkImportPayeesRow): PayeeInput => {
  const dueDates =
    row.payeeType === 'TAX'
      ? compact([row.dueDate1, row.dueDate2, row.dueDate3, row.dueDate4]).filter((d): d is string => d !== '')
      : [];

  return PayeeInput.parse({
    name: row.name,
    payeeType: row.payeeType,
    taxType: row.payeeType === 'TAX' ? row.taxType : undefined,
    externalPayeeId: row.externalPayeeId || undefined,
    paymentMethod: row.paymentMethod,
    phone: row.phone || undefined,
    address: isAddressBlank(row) ? undefined : rowToAddress(row),
    dueDates,
    isActive: row.isActive,
  });
};
